import { FC, memo, useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import { RangeFilter } from "@/components/filters/Ranges/RangeFilter";
import { useScoringStore } from "@/store/scoringStore";
import type { CompanyFilters, FilterChip } from "../types";
import { useCompanyFiltersStore } from "../provider/CompanyFilterProvider";
import { buildRangeLabel } from "../utils/buildRangeLabel";
import { getScoringFilterStatus } from "./scoringFilterStatus";
import type { FilterModule } from "./index";
import type { ScoreSlice } from "./fitScore";

// ── Slice ────────────────────────────────────────────────────────────────────

const defaultSlice: ScoreSlice = { min: undefined, max: undefined };

// ── Mapper ───────────────────────────────────────────────────────────────────

function toCompanyFilters(slice: ScoreSlice): Partial<CompanyFilters> {
  if (slice.min === undefined && slice.max === undefined) return {};
  return { intentScore: { min: slice.min, max: slice.max } };
}

function fromCompanyFilters(filters: CompanyFilters): ScoreSlice {
  const range = filters.intentScore as
    | { min?: number; max?: number }
    | undefined;
  if (!range) return defaultSlice;
  return { min: range.min ?? undefined, max: range.max ?? undefined };
}

// ── Chips ────────────────────────────────────────────────────────────────────

function getChips(
  slice: ScoreSlice,
  setSlice: (slice: ScoreSlice) => void
): FilterChip[] {
  if (slice.min === undefined && slice.max === undefined) return [];
  return [
    {
      id: "intentScore",
      label: `Intent Score: ${buildRangeLabel(slice.min, slice.max)}`,
      filterKey: "intentScore",
      onRemove: () => setSlice(defaultSlice),
    },
  ];
}

function getCount(slice: ScoreSlice): number {
  return slice.min !== undefined || slice.max !== undefined ? 1 : 0;
}

// ── Component ────────────────────────────────────────────────────────────────

const DEBOUNCE_MS = 500;

const IntentScoreFilter: FC = memo(() => {
  const slice = useCompanyFiltersStore(
    (s) => s.slices.intentScore
  ) as ScoreSlice;
  const setSlice = useCompanyFiltersStore((s) => s.setSlice);

  const activeProfileIds = useScoringStore((s) => s.activeProfileIds);
  const profiles = useScoringStore((s) => s.profiles);
  const { enabled, disabledReason } = getScoringFilterStatus(
    "intent",
    activeProfileIds,
    profiles
  );

  const [localMin, setLocalMin] = useState(slice.min);
  const [localMax, setLocalMax] = useState(slice.max);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    setLocalMin(slice.min);
    setLocalMax(slice.max);
  }, [slice.min, slice.max]);

  const debouncedSetSlice = useCallback(
    (min: number | undefined, max: number | undefined) => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setSlice("intentScore", { min, max });
      }, DEBOUNCE_MS);
    },
    [setSlice]
  );

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const handleRangeChange = useCallback(
    (min: number | undefined, max: number | undefined) => {
      setLocalMin(min);
      setLocalMax(max);
      debouncedSetSlice(min, max);
    },
    [debouncedSetSlice]
  );

  const handleClear = useCallback(() => {
    clearTimeout(debounceRef.current);
    setLocalMin(undefined);
    setLocalMax(undefined);
    setSlice("intentScore", defaultSlice);
  }, [setSlice]);

  const hasValues = localMin !== undefined || localMax !== undefined;

  return (
    <Box
      id="intentScore-filter-container"
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      {!enabled && disabledReason && (
        <Alert severity="info" sx={{ fontSize: "0.8125rem" }}>
          {disabledReason}
        </Alert>
      )}

      <RangeFilter
        value={{ min: localMin, max: localMax }}
        onChange={handleRangeChange}
        isDisabled={!enabled}
        updatedOnType
      />

      {hasValues && (
        <Button
          size="small"
          onClick={handleClear}
          disabled={!enabled}
          sx={{ alignSelf: "flex-start" }}
        >
          Clear
        </Button>
      )}
    </Box>
  );
});

IntentScoreFilter.displayName = "IntentScoreFilter";

// ── Module ───────────────────────────────────────────────────────────────────

export const intentScoreModule: FilterModule<ScoreSlice> = {
  key: "intentScore",
  defaultSlice,
  Component: IntentScoreFilter,
  toCompanyFilters,
  fromCompanyFilters,
  getChips,
  getCount,
};

export const defaultIntentScoreSlice = defaultSlice;
